<script lang="ts">
  import { BALANCE, BOT_DIFFICULTIES, type BotDifficulty } from '@2k48/shared';
  import { createMotionScope, enterFrom, staggerIn } from '../../game/motion';
  import { feedback } from '../../platform/feedback';
  import ModeButton from '../components/ModeButton.svelte';
  import PixelAvatar from '../components/PixelAvatar.svelte';
  import PixelButton from '../components/PixelButton.svelte';
  import PixelIcon from '../components/PixelIcon.svelte';
  import { i18n } from '../i18n/index.svelte';
  import { router } from '../stores/router.svelte';
  import { session } from '../stores/session.svelte';
  import { groupNumber } from '../format';

  const t = $derived(i18n.t);

  let panel = $state<'modes' | 'difficulty' | 'private' | 'code'>('modes');

  /**
   * The four opponents, in the order `BOT_DIFFICULTIES` declares — weakest
   * first, so the list reads as a ramp rather than a menu.
   *
   * Tone climbs with it. Green reads as safe and magenta as the one that will
   * hurt, which is a second, wordless statement of the same ordering for
   * somebody scanning rather than reading.
   */
  const DIFFICULTY_TONES: Record<BotDifficulty, 'grn' | 'cyan' | 'yel' | 'mag'> = {
    easy: 'grn',
    medium: 'cyan',
    hard: 'yel',
    insane: 'mag',
  };
  let code = $state('');
  let panelRoot: HTMLElement | undefined = $state();
  /**
   * Which way the next panel arrives. Going deeper (modes → private → code)
   * slides in from the right; backing out comes from the left, so the gesture
   * matches the direction of travel instead of every panel appearing the same
   * way regardless of where the player came from.
   */
  let direction = $state<'left' | 'right'>('right');
  const alphabet = [...BALANCE.matchmaking.privateCodeAlphabet];

  const scope = createMotionScope();

  $effect(() => {
    // Reading `panel` is the subscription; the tween runs on every change.
    const current = panel;
    const from = direction;
    if (!panelRoot) return;

    scope.add(() => {
      const rows = [...panelRoot!.children] as HTMLElement[];
      // The mode list is short enough to stagger; the keyboard is not, and
      // `staggerIn` drops the stagger past its own threshold anyway.
      if (current === 'modes') staggerIn(rows, from);
      else enterFrom(panelRoot!, from);
    });
  });

  $effect(() => () => scope.destroy());

  function goTo(next: typeof panel, way: 'left' | 'right'): void {
    direction = way;
    panel = next;
  }

  function back(): void {
    if (panel === 'modes') router.go('menu');
    else if (panel === 'code') goTo('private', 'left');
    else goTo('modes', 'left');
  }

  const complete = $derived(code.length === BALANCE.matchmaking.privateCodeLength);

  /**
   * The keypad is the one place a click carries information beyond "pressed":
   * the delegated UI click sound fires for every button, so what is added here
   * is only the two states it cannot know about — the code just became
   * complete, or the key did nothing because it was already full.
   */
  function addCharacter(character: string): void {
    if (code.length >= BALANCE.matchmaking.privateCodeLength) {
      feedback.emit('ui.invalid');
      return;
    }

    code += character;
    if (code.length === BALANCE.matchmaking.privateCodeLength) feedback.emit('ui.confirm');
  }

  function deleteCharacter(): void {
    if (code.length === 0) {
      feedback.emit('ui.invalid');
      return;
    }
    code = code.slice(0, -1);
  }
</script>

<div class="view play">
  <header class="view__header">
    <button class="topbar__back" onclick={back} aria-label="Volver">
      <PixelIcon name="back" size={28} />
    </button>
    <h1 class="view__title">
      {panel === 'code'
        ? t.play.enterCode
        : panel === 'difficulty'
          ? t.play.modes.botTitle
          : t.menu.play}
    </h1>
    <span class="topbar__spacer" aria-hidden="true"></span>
  </header>

  {#if panel === 'modes'}
    <div class="play__profile">
      <PixelAvatar avatarId={session.profile?.avatarId ?? 0} size="sm" />
      <strong>{session.profile?.displayName ?? 'INVITADO'}</strong>
      <!-- The real rating or nothing. A placeholder 1000 reads as a rank the
           player earned, and is the number they would quote back at you. -->
      <span>{session.rating === null ? 'SIN CLASIFICAR' : `ELO ${session.rating}`}</span>
    </div>

    <div class="play__list scroll-y" bind:this={panelRoot}>
      <p class="play__section play__section--multi">{t.play.multiplayer}</p>
      <ModeButton
        icon="swords"
        title={t.play.modes.matchTitle}
        subtitle={t.play.modes.matchHint}
        tone="mag"
        featured
        onclick={() => router.go('versus')}
      />
      <ModeButton
        icon="bot"
        title={t.play.modes.botTitle}
        subtitle={t.play.modes.botHint}
        tone="cyan"
        onclick={() => goTo('difficulty', 'right')}
      />
      <ModeButton
        icon="lock"
        title={t.play.modes.privateTitle}
        subtitle={t.play.modes.privateHint}
        tone="yel"
        onclick={() => goTo('private', 'right')}
      />

      <p class="play__section play__section--solo">{t.play.singlePlayer}</p>
      <ModeButton
        icon="timer"
        title={t.play.modes.timedTitle}
        subtitle={t.play.modes.timedHint}
        tone="grn"
        onclick={() => router.go('solo', { mode: 'timed' })}
      />
      <ModeButton
        icon="infinity"
        title={t.play.modes.endlessTitle}
        subtitle={t.play.modes.endlessHint}
        tone="blu"
        onclick={() => router.go('solo', { mode: 'endless' })}
      />

      <!-- Omitted rather than shown as 0 until the score is known: a record of
           zero reads as "you have played and scored nothing". -->
      {#if session.bestScore !== null && session.bestScore > 0}
        <p class="tiny play__record">{t.play.personalBest(groupNumber(session.bestScore))}</p>
      {/if}
    </div>
  {:else if panel === 'difficulty'}
    <div class="play__list scroll-y" bind:this={panelRoot}>
      <p class="play__section play__section--multi">{t.play.chooseDifficulty}</p>
      {#each BOT_DIFFICULTIES as difficulty (difficulty)}
        <ModeButton
          icon="bot"
          title={t.play.difficulties[difficulty].title}
          subtitle={t.play.difficulties[difficulty].hint}
          tone={DIFFICULTY_TONES[difficulty]}
          onclick={() => router.go('versus', { mode: 'bot', difficulty })}
        />
      {/each}
      <p class="tiny play__record">{t.play.difficultyFootnote}</p>
    </div>
  {:else if panel === 'private'}
    <div class="play__private" bind:this={panelRoot}>
      <p class="play__private-title">PARTIDA PRIVADA</p>
      <p class="tiny">{t.play.privateIntro}</p>
      <PixelButton
        variant="accent"
        icon="lock"
        onclick={() => router.go('versus', { queue: 'private-create' })}
        >{t.play.createRoom}</PixelButton
      >
      <PixelButton variant="p1" icon="play" onclick={() => goTo('code', 'right')}
        >{t.play.enterCode}</PixelButton
      >
    </div>
  {:else}
    <div class="play__code" bind:this={panelRoot}>
      <p class="lbl">{t.play.codePrompt}</p>
      <div class="play__codeboxes" aria-label={`Codigo ${code}`}>
        {#each { length: BALANCE.matchmaking.privateCodeLength } as _, index (index)}
          <span class="codebox" class:codebox--active={index === code.length}
            >{code[index] ?? (index === code.length ? '_' : '')}</span
          >
        {/each}
      </div>
      <p class="tiny play__code-hint">
        {BALANCE.matchmaking.privateCodeLength} CARACTERES · LETRAS Y NUMEROS
      </p>
      <div class="play__keyboard">
        {#each alphabet as character (character)}
          <button class="play__key" onclick={() => addCharacter(character)}>{character}</button>
        {/each}
        <button class="play__key play__key--delete" onclick={deleteCharacter}>
          <PixelIcon name="delete" size={28} />
        </button>
      </div>
      <PixelButton
        variant="p1"
        icon="play"
        disabled={!complete}
        onclick={() => router.go('versus', { queue: 'private-join', code })}
        >{t.play.join}</PixelButton
      >
      {#if !complete}
        <p class="tiny play__code-hint">{t.play.codeIncomplete}</p>
      {/if}
    </div>
  {/if}
</div>
